import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Video, Upload, Trash2, ArrowLeft, ArrowRight, Check, Zap, AlertCircle } from "lucide-react";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import {
    uploadVideo,
    uploadImage,
    listMyVideos,
    createMyVideo,
    deleteMyVideo,
    VIDEO_STATUS_TONE,
    humanBytes,
    formatDur,
} from "@/lib/ads";
import { formatDate } from "@/lib/format";

const MIN_SEC = 5;
const MAX_SEC = 35;
const MAX_MB = 30;

export default function MerchantVideosPage() {
    const navigate = useNavigate();
    const [slots, setSlots] = useState({ video_ad_slots: 0, package_slug: null });
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wizardOpen, setWizardOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [sub, vids] = await Promise.all([
                api.get("/me/subscription").catch(() => ({ data: null })),
                listMyVideos(),
            ]);
            const s = sub.data;
            setSlots({
                video_ad_slots: s?.package?.features?.video_ad_slots || 0,
                package_slug: s?.subscription?.package_slug_snapshot || s?.package?.slug,
            });
            setVideos(vids || []);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const activeCount = useMemo(
        () => videos.filter((v) => ["PENDING_REVIEW", "APPROVED", "LIVE"].includes(v.status)).length,
        [videos],
    );
    const remaining = Math.max(0, slots.video_ad_slots - activeCount);

    if (!loading && slots.video_ad_slots === 0) {
        return (
            <div className="p-4" data-testid="merchant-videos-empty">
                <Card className="mx-auto max-w-md rounded-2xl border border-neutral-200 p-6 text-center">
                    <Video size={32} className="mx-auto text-neutral-400" />
                    <h2 className="mt-2 text-lg font-bold text-neutral-900">Video Ads</h2>
                    <p className="mt-1 text-sm text-neutral-500">
                        Your current plan ({slots.package_slug || "Bronze"}) doesn't include video ad slots.
                        Upgrade to Silver, Gold or Platinum to unlock this feature.
                    </p>
                    <Link to="/merchant/home">
                        <Button className="mt-3 nest-gradient text-white" data-testid="videos-upgrade-btn">
                            View plans
                        </Button>
                    </Link>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-4" data-testid="merchant-videos-page">
            <div className="mb-4">
                <h1 className="text-2xl font-extrabold text-neutral-900">Video Ads</h1>
                <p className="mt-1 text-sm text-neutral-500">
                    You can have up to <b>{slots.video_ad_slots}</b> live video ads at a time.
                </p>
            </div>

            <Card className="mb-4 rounded-2xl border border-neutral-200 p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-xs font-medium text-neutral-500">Slots used</div>
                        <div className="text-2xl font-extrabold text-neutral-900">
                            {activeCount} / {slots.video_ad_slots}
                        </div>
                    </div>
                    <Button
                        className="nest-gradient text-white"
                        onClick={() => setWizardOpen(true)}
                        data-testid="videos-upload-btn"
                    >
                        <Upload size={14} className="mr-2" /> Upload New Video
                    </Button>
                </div>
                <Progress
                    className="mt-3 h-2"
                    value={slots.video_ad_slots ? (activeCount / slots.video_ad_slots) * 100 : 0}
                />
                {remaining === 0 && (
                    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                        <AlertCircle size={12} className="inline mr-1" />
                        Slot cap reached. Delete a rejected or paused ad, or wait for one to expire.
                    </div>
                )}
            </Card>

            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-72 rounded-2xl" />
                    ))}
                </div>
            ) : videos.length === 0 ? (
                <Card className="rounded-2xl border border-neutral-200 p-10 text-center">
                    <Video size={32} className="mx-auto text-neutral-400" />
                    <div className="mt-2 text-sm font-semibold text-neutral-700">No video ads yet</div>
                    <div className="text-xs text-neutral-500">Upload a 5–35s video to reach more customers.</div>
                </Card>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                    {videos.map((v) => (
                        <MyVideoCard key={v.id} v={v} onChanged={load} />
                    ))}
                </div>
            )}

            <UploadWizard
                open={wizardOpen}
                onOpenChange={setWizardOpen}
                onSubmitted={() => {
                    setWizardOpen(false);
                    load();
                }}
            />
        </div>
    );
}

function MyVideoCard({ v, onChanged }) {
    const canDelete = !["LIVE"].includes(v.status);
    const del = async () => {
        if (!window.confirm("Delete this video ad?")) return;
        try {
            await deleteMyVideo(v.id);
            toast.success("Deleted");
            onChanged?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    return (
        <Card className="overflow-hidden rounded-2xl border border-neutral-200" data-testid={`my-video-${v.id}`}>
            <div className="relative bg-neutral-900">
                <video
                    src={publicAssetUrl(v.video_url)}
                    poster={v.thumbnail_url ? publicAssetUrl(v.thumbnail_url) : undefined}
                    controls
                    muted
                    preload="metadata"
                    className="aspect-video w-full bg-black"
                />
                <Badge variant="outline" className={`absolute top-2 right-2 text-[10px] uppercase ${VIDEO_STATUS_TONE[v.status] || ""}`}>
                    {v.status.replace("_", " ")}
                </Badge>
            </div>
            <div className="p-3">
                <div className="text-sm font-bold">{v.title}</div>
                <div className="mt-1 text-[11px] text-neutral-500">
                    {formatDate(v.start_date)} → {formatDate(v.end_date)} · {formatDur(v.duration_seconds)}
                </div>
                {v.status === "LIVE" && (
                    <div className="mt-1 text-[11px] text-neutral-600">
                        {v.impressions_count} views · {v.clicks_count} clicks
                    </div>
                )}
                {v.rejection_reason && (
                    <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-800">
                        <b>Rejected:</b> {v.rejection_reason}
                    </div>
                )}
                {canDelete && (
                    <Button size="sm" variant="outline" className="mt-2 text-rose-600" onClick={del} data-testid={`my-video-del-${v.id}`}>
                        <Trash2 size={12} className="mr-1" /> Delete
                    </Button>
                )}
            </div>
        </Card>
    );
}

function UploadWizard({ open, onOpenChange, onSubmitted }) {
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [videoUrl, setVideoUrl] = useState("");
    const [thumbUrl, setThumbUrl] = useState("");
    const [durationSec, setDurationSec] = useState(0);
    const [fileSize, setFileSize] = useState(0);
    const [uploadPct, setUploadPct] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [title, setTitle] = useState("");
    const [linkedPostId, setLinkedPostId] = useState("");
    const [myPosts, setMyPosts] = useState([]);
    const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState(new Date(Date.now() + 14 * 86400 * 1000).toISOString().slice(0, 10));
    const [busy, setBusy] = useState(false);
    const videoElRef = useRef(null);

    useEffect(() => {
        if (open) {
            setStep(1); setFile(null); setVideoUrl(""); setThumbUrl(""); setDurationSec(0);
            setFileSize(0); setUploadPct(0); setUploading(false); setTitle("");
            setLinkedPostId(""); setBusy(false);
        }
    }, [open]);

    useEffect(() => {
        if (open && step === 2 && myPosts.length === 0) {
            api.get("/me/posts").then((r) => {
                const arr = Array.isArray(r.data) ? r.data : r.data?.posts || [];
                setMyPosts(arr.filter((p) => p.status === "PUBLISHED"));
            }).catch(() => {});
        }
    }, [open, step, myPosts.length]);

    const generateThumb = (file, duration) =>
        new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const video = document.createElement("video");
            video.preload = "metadata";
            video.muted = true;
            video.src = url;
            video.currentTime = Math.min(0.5, duration - 0.1);
            video.onloadeddata = () => {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth || 640;
                canvas.height = video.videoHeight || 360;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(url);
                    if (!blob) return reject(new Error("thumbnail failed"));
                    resolve(new File([blob], "thumb.jpg", { type: "image/jpeg" }));
                }, "image/jpeg", 0.8);
            };
            video.onerror = () => { URL.revokeObjectURL(url); reject(new Error("video load failed")); };
        });

    const onFilePicked = async (f) => {
        if (!f) return;
        if (!/(mp4|quicktime)/.test(f.type)) return toast.error("Only mp4 / mov videos accepted");
        if (f.size > MAX_MB * 1024 * 1024) return toast.error(`Max ${MAX_MB}MB`);
        setFile(f);
        setFileSize(f.size);

        // Read duration via HTML5
        const url = URL.createObjectURL(f);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.src = url;
        await new Promise((resolve) => (video.onloadedmetadata = resolve));
        const dur = Math.round(video.duration || 0);
        URL.revokeObjectURL(url);
        if (dur < MIN_SEC || dur > MAX_SEC) {
            setFile(null);
            return toast.error(`Video must be ${MIN_SEC}–${MAX_SEC} seconds (got ${dur}s)`);
        }
        setDurationSec(dur);

        // Upload video + generated thumb in parallel
        setUploading(true);
        try {
            const [vres, thumbFile] = await Promise.all([
                uploadVideo(f, setUploadPct),
                generateThumb(f, dur).catch(() => null),
            ]);
            setVideoUrl(vres.url);
            if (thumbFile) {
                try {
                    const tres = await uploadImage(thumbFile, "poster");
                    setThumbUrl(tres.url);
                } catch (_) {}
            }
            toast.success("Uploaded — next step");
            setStep(2);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setUploading(false);
        }
    };

    const submit = async () => {
        if (!title.trim()) return toast.error("Title required");
        if (!videoUrl) return toast.error("Please upload a video");
        try {
            setBusy(true);
            await createMyVideo({
                video_url: videoUrl,
                thumbnail_url: thumbUrl || null,
                duration_seconds: durationSec,
                file_size_bytes: fileSize,
                title: title.trim(),
                linked_post_id: linkedPostId || null,
                start_date: startDate,
                end_date: endDate,
            });
            toast.success("Video ad submitted for review");
            onSubmitted?.();
        } catch (e) {
            const s = e?.response?.status;
            if (s === 429) {
                toast.error("You've reached your plan's video ad limit. Delete a paused/rejected ad or upgrade your plan.");
            } else if (s === 403) {
                toast.error("Your plan doesn't include video ad slots. Upgrade to submit videos.");
            } else {
                toast.error(errorMessage(e));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="video-wizard-dialog">
                <DialogHeader>
                    <DialogTitle>Upload a video ad · Step {step} of 3</DialogTitle>
                    <DialogDescription>5–35 seconds · mp4 or mov · up to 30 MB</DialogDescription>
                </DialogHeader>

                {step === 1 && (
                    <div>
                        <input
                            type="file"
                            accept="video/mp4,video/quicktime"
                            capture="user"
                            onChange={(e) => onFilePicked(e.target.files?.[0])}
                            className="block w-full rounded-xl border-2 border-dashed border-neutral-300 p-6 text-sm"
                            data-testid="video-file-input"
                        />
                        {uploading && (
                            <div className="mt-3">
                                <div className="mb-1 text-xs font-semibold text-orange-600">Uploading… {uploadPct}%</div>
                                <Progress value={uploadPct} className="h-2" />
                            </div>
                        )}
                        {file && !uploading && (
                            <div className="mt-3 text-xs text-neutral-600">
                                {file.name} · {humanBytes(file.size)} · {formatDur(durationSec)}
                            </div>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div className="grid gap-3">
                        {videoUrl && (
                            <video
                                src={publicAssetUrl(videoUrl)}
                                controls
                                muted
                                className="aspect-video w-full rounded-lg bg-black"
                            />
                        )}
                        <div>
                            <Label>Title *</Label>
                            <Input
                                maxLength={120}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                data-testid="video-title-input"
                            />
                        </div>
                        <div>
                            <Label>Linked offer (optional)</Label>
                            <Select value={linkedPostId || "__none__"} onValueChange={(v) => setLinkedPostId(v === "__none__" ? "" : v)}>
                                <SelectTrigger data-testid="video-linked-post-select"><SelectValue placeholder="No linked offer" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— None —</SelectItem>
                                    {myPosts.map((p) => (
                                        <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Start date</Label>
                                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="video-start-input" />
                            </div>
                            <div>
                                <Label>End date</Label>
                                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="video-end-input" />
                            </div>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="rounded-xl border border-neutral-200 p-3 text-sm">
                        <div className="font-bold">{title}</div>
                        <div className="text-xs text-neutral-500">
                            {startDate} → {endDate} · {formatDur(durationSec)} · {humanBytes(fileSize)}
                        </div>
                        {videoUrl && (
                            <video src={publicAssetUrl(videoUrl)} controls muted className="mt-2 aspect-video w-full rounded-lg bg-black" />
                        )}
                        {linkedPostId && (
                            <div className="mt-2 text-xs text-neutral-600">
                                Linked to offer <span className="font-mono">{linkedPostId.slice(0, 8)}…</span>
                            </div>
                        )}
                        <div className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                            After submitting, your ad will be <b>PENDING_REVIEW</b> — an admin will approve or reject it, and you'll be notified.
                        </div>
                    </div>
                )}

                <DialogFooter className="flex-col gap-2 sm:flex-row">
                    {step > 1 && (
                        <Button variant="outline" onClick={() => setStep((s) => s - 1)}>
                            <ArrowLeft size={14} className="mr-1" /> Back
                        </Button>
                    )}
                    {step < 3 && step > 1 && (
                        <Button
                            className="nest-gradient text-white"
                            onClick={() => (title.trim() ? setStep(step + 1) : toast.error("Title required"))}
                            disabled={!title.trim()}
                            data-testid={`video-wizard-next-${step}`}
                        >
                            Next <ArrowRight size={14} className="ml-1" />
                        </Button>
                    )}
                    {step === 3 && (
                        <Button
                            className="nest-gradient text-white"
                            onClick={submit}
                            disabled={busy}
                            data-testid="video-wizard-submit"
                        >
                            <Check size={14} className="mr-1" /> {busy ? "Submitting…" : "Submit for review"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
